import { useSyncExternalStore } from 'react'

// Перехват `beforeinstallprompt` (Android Chrome). Слушатель вешаем на уровне
// модуля, а НЕ в эффекте: событие фаерится рано — до того как пользователь войдёт
// и смонтируется AppLayout с модалкой. Импортируется side-effect'ом в main.tsx,
// чтобы подписка встала на старте приложения.

let deferred: BeforeInstallPromptEvent | null = null
const subscribers = new Set<() => void>()

function emit() {
  for (const cb of subscribers) cb()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Гасим автоматический мини-инфобар Chrome, чтобы вызвать prompt() по кнопке.
    event.preventDefault()
    deferred = event
    emit()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    emit()
  })
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb)
  return () => subscribers.delete(cb)
}

function getSnapshot(): boolean {
  return deferred !== null
}

interface UseInstallPrompt {
  // Доступен ли программный нативный запрос установки (Android Chrome с активным SW).
  canPromptInstall: boolean
  // Показать системный диалог установки. Возвращает выбор пользователя
  // ('accepted' | 'dismissed') либо null, если запрос недоступен.
  promptInstall: () => Promise<'accepted' | 'dismissed' | null>
}

export function useInstallPrompt(): UseInstallPrompt {
  const canPromptInstall = useSyncExternalStore(subscribe, getSnapshot)

  const promptInstall = async (): Promise<'accepted' | 'dismissed' | null> => {
    if (!deferred) return null
    await deferred.prompt()
    const choice = await deferred.userChoice
    // Событие одноразовое — Chrome не выдаёт его повторно после prompt().
    deferred = null
    emit()
    return choice.outcome
  }

  return { canPromptInstall, promptInstall }
}
